import { publish } from 'pubsub-js';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FileModel } from '../../../../../../../../typings/api/skymap/rest/v1/.common';
import { CreateImageRequestBody } from '../../../../../../../../typings/api/skymap/rest/v1/project';
import { TokenService } from '../../../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { downloadFile } from '../../../../../../services/axios/utility/download-file';
import { FileUpload } from '../../../../../../services/network/file-upload';
import { WebApi } from '../../../../../../services/web-api';
import { isDefined, isIncluded } from '../../../../../../utils/variables';
import { AssetManager } from '../../../../../../viewer/asset-manager';
import { Poi } from '../../../../../../viewer/elements/poi/poi';
import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { SceneManager } from '../../../../../../viewer/scene-manager';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useNonce } from '../../../../../hooks/use-nonce';
import { useProjectSnapshot } from '../../../../../hooks/use-project-snapshot';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { imageCompatibleFormat } from '../../../../../utils/file';
import { getFileExtension } from '../../../../../utils/path';
import { reset } from '../../../../../utils/styled-reset';
import { ConfirmationDialog } from '../../../../confirmation-dialog/confirmation-dialog';
import { Dialog } from '../../../../dialog/dialog';
import { DropdownButton, DropDownButtonOptions } from '../../../../dropdown-button';
import { FileManagerSelectionDialog } from '../../../../file-manager/dialogs/file-manager-selection-dialog/file-manager-selection-dialog';
import { Icon } from '../../../../icon/icon';
import { IconPanel } from '../../../../icon-panel/icon-panel';
import { InfoBox } from '../../../../info-box/info-box';
import { LoadingProgress } from '../../../../loading-progress';
import { Stack } from '../../../../stack/stack';
import { Switch } from '../../../../switch/switch';
import { DeleteIcon } from '../../icons/delete-icon';
import { DownloadIcon } from '../../icons/download-icon';
import { PanoramaIcon } from '../../icons/panorama-icon';

interface Props {
  poi: Poi;
  onClose: () => void;
}

enum PoiImageDialogStatus {
  Idle,
  Creating,
  Removing,
}

const PoiImageDialog = (props: Props) => {
  const { projectId } = useProjectSnapshot();
  const { isSharedView } = useSharedView();
  const { t } = useTranslation();
  const saveButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [_, refresh] = useNonce();

  const [dialogStatus, setDialogStatus] = React.useState<PoiImageDialogStatus>(
    PoiImageDialogStatus.Idle,
  );
  const [invalidFileFormat, setInvalidFileFormat] = React.useState<string | undefined>();
  const [currentLoadingStep, setCurrentLoadingStep] = React.useState<number | undefined>(undefined);
  const fileManagerDialog = useDialog();
  const removeImageVerificationDialog = useDialog();

  const [is360, setIs360] = React.useState(props.poi.properties.is360);
  const [isUpdating360, setIsUpdating360] = React.useState<boolean>(false);

  useSubscribe(SubscriptionTopic.AssetChanged, (messageAsset) => {
    if (props.poi.assetId === messageAsset?.assetId) {
      refresh();
    }
  });

  const poiImageCreationSteps: string[] = [
    t('poiImageDialog.createImageSteps.uploading', { ns: 'components' }),
    t('poiImageDialog.createImageSteps.creating', { ns: 'components' }),
    t('poiImageDialog.createImageSteps.connectImage', { ns: 'components' }),
    t('poiImageDialog.createImageSteps.complete', { ns: 'components' }),
  ];

  const poiImageRemovalSteps: string[] = [
    t('poiImageDialog.deleteImageSteps.removing', { ns: 'components' }),
    t('poiImageDialog.deleteImageSteps.removed', { ns: 'components' }),
  ];

  const poiHasThumbnail = isDefined(props.poi.properties.thumbnailImage);
  const openImageOriginal = () => {
    const originalPreviewUrl = props.poi.getImagePreviewUrl();
    window.open(originalPreviewUrl, '_blank', 'noreferrer');
  };

  function resolvePoiImageName(poi: Poi): string {
    const extension = poi.properties.thumbnailImage?.imageType?.toLowerCase();
    if (isDefined(extension) && !poi.name.toLowerCase().endsWith(`.${extension}`)) {
      return `${poi.name}.${extension}`;
    }
    return poi.name;
  }
  const downloadImageOriginal = async () => {
    const originalThumbnailUrl = props.poi.getThumbnailResourceUrl('original');
    if (isDefined(originalThumbnailUrl)) {
      await downloadFile(originalThumbnailUrl, resolvePoiImageName(props.poi));
    }
  };
  const removePoiImage = async () => {
    setDialogStatus(PoiImageDialogStatus.Removing);

    setCurrentLoadingStep(0);
    const poiServiceV1 = SkyMapAxiosServiceFactory.instance.createPoiServiceV1();
    await poiServiceV1.removeAssociatedImageFromPoi({ path: { poiId: props.poi.assetId! } });

    setCurrentLoadingStep(1);
    setTimeout(async () => {
      setDialogStatus(PoiImageDialogStatus.Idle);
      await AssetManager.instance.updateAsset(props.poi.assetId!, 'Poi');
      AssetManager.instance.publishAssetListChanged();
    }, 1000);
  };

  const resolveImageDialogIcons = () => {
    if (!poiHasThumbnail) {
      return undefined;
    }
    return (
      <>
        <DownloadIcon key="downloadIcon" onClick={downloadImageOriginal} />
        {props.poi.is360Poi() && (
          <PanoramaIcon
            key="panoramaIcon"
            onClick={async (e) => {
              await SceneManager.instance.setScene('panoramaViewer');
              publish(SubscriptionTopic.AssetChanged, props.poi);

              props.onClose();
            }}
          />
        )}
        <DeleteIcon key="deleteIcon" onClick={() => removeImageVerificationDialog.show()} />
      </>
    );
  };
  const renderThumbnail = () => (
    <ImageWrapper>
      <PreviewImageComponent
        data-testid="preview-image"
        src={props.poi.getThumbnailResourceUrl('512x_')}
        onClick={openImageOriginal}
      />

      {/** Show only when not shared and we have an image */}
      {!isSharedView && (
        <SwitchOverlay
          aria-label="Toggle 360°"
          title="360°"
          onClick={(e) => e.stopPropagation()} // prevent opening the original image
        >
          {isUpdating360 ? (
            <Icon icon={['fas', 'spinner']} size="xs" spin={true} />
          ) : (
            <SwitchText>360°</SwitchText>
          )}
          <Switch checked={is360} onChange={() => void toggleIs360()} />
        </SwitchOverlay>
      )}
    </ImageWrapper>
  );

  const renderNoThumbnail = () => (
    <NoImageComponent direction="column">
      <IconPanel
        header={{ icon: { icon: ['fad', 'image'], opacity: '50%', size: '10x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('poiImageDialog.noImageAssociated.title', { ns: 'components' }),
          info: (
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="poiImageDialog.noImageAssociated.text"
              ns="components"
            />
          ),
        }}
      </IconPanel>
    </NoImageComponent>
  );

  const renderCreateImageLoading = () => (
    <LoadingProgress
      currentStep={currentLoadingStep ?? 0}
      steps={poiImageCreationSteps}
      title={t('poiImageDialog.createImageSteps.title', { ns: 'components' })}
    />
  );

  const renderRemoveImageLoading = () => (
    <LoadingProgress
      currentStep={currentLoadingStep ?? 0}
      steps={poiImageRemovalSteps}
      title={t('poiImageDialog.deleteImageSteps.title', { ns: 'components' })}
    />
  );

  const toggleIs360 = React.useCallback(async () => {
    if (isSharedView || !isDefined(props.poi.assetId)) {
      return;
    }

    const prev = is360;
    setIsUpdating360(true);

    try {
      props.poi.properties.is360 = !is360;
      await props.poi.save();
      setIs360(!is360);
    } catch (e) {
      console.error('Failed to toggle is360 for POI', e);
      props.poi.properties.is360 = prev;
      setIs360(prev);
    } finally {
      setIsUpdating360(false);
    }
  }, [is360, isSharedView, props.poi]);

  const renderContent = () => {
    return (
      <Stack spacing={1}>
        {dialogStatus === PoiImageDialogStatus.Creating
          ? renderCreateImageLoading()
          : dialogStatus === PoiImageDialogStatus.Removing
            ? renderRemoveImageLoading()
            : poiHasThumbnail
              ? renderThumbnail()
              : renderNoThumbnail()}
        {isDefined(invalidFileFormat) && (
          <ErrorContainer>
            <InfoBox
              color="red"
              leftIcon={{ icon: 'info' }}
              title={t('poiImageDialog.invalidImageFormatTitle', { ns: 'components' })}
            >
              <Trans
                components={{
                  bold: <strong />,
                  br: <br />,
                }}
                i18nKey="poiImageDialog.invalidImageFormat"
                ns="components"
                values={{
                  fileExtension: invalidFileFormat,
                  validFileFormats: `[${imageCompatibleFormat.join(', ')}]`,
                }}
              />
            </InfoBox>
          </ErrorContainer>
        )}
      </Stack>
    );
  };

  const resolvesTitle = () =>
    props.poi.properties.thumbnailImage?.name ??
    t('poiImageDialog.noImageHeader', { ns: 'components' });

  const handleAssociateImageSelection = (option: DropDownButtonOptions) => {
    switch (option.value) {
      case 'Datorn':
        fileInputRef.current?.click();
        break;
      case 'Filhanteraren':
        fileManagerDialog.show();
        break;
      default:
        break;
    }
  };

  const updatePoiWithImage = React.useCallback(
    async (requestPayload: CreateImageRequestBody) => {
      const projectServiceV1 = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const { data: image } = await projectServiceV1.createImage({
        path: { projectId: projectId! },
        body: requestPayload,
      });

      setCurrentLoadingStep(2);
      const poiServiceV1 = SkyMapAxiosServiceFactory.instance.createPoiServiceV1();
      await poiServiceV1.associateImageWithPoi({
        path: { poiId: props.poi.assetId!, imageId: image.id },
      });

      setCurrentLoadingStep(3);
      setTimeout(() => setDialogStatus(PoiImageDialogStatus.Idle), 1000);
    },
    [projectId, props.poi.assetId],
  );

  const onLocalImageSelected = React.useCallback(async () => {
    const files = fileInputRef.current?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const upload = new FileUpload({
        apiBaseUrl: WebApi.getUrl(),
        authToken: new TokenService().get(),
        file: file,
        concurrency: 1,
      });

      setDialogStatus(PoiImageDialogStatus.Creating);
      setCurrentLoadingStep(0);

      const uploadId = await upload.start();

      setCurrentLoadingStep(1);
      await updatePoiWithImage({ name: file.name, uploadId: uploadId.id });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [updatePoiWithImage]);

  const onFileManagerImageSelected = React.useCallback(
    async (files: FileModel[]) => {
      const file = files[0];
      const fileVersion = file?.versions[0];
      if (!isDefined(file) || !isDefined(fileVersion)) {
        return;
      } else if (!isIncluded(imageCompatibleFormat, getFileExtension(file.name))) {
        setInvalidFileFormat(getFileExtension(file.name).toLowerCase());
        return;
      }

      setInvalidFileFormat(undefined);

      setDialogStatus(PoiImageDialogStatus.Creating);
      setCurrentLoadingStep(1);

      await updatePoiWithImage({ name: file.name, fileVersionId: fileVersion.id });
    },
    [updatePoiWithImage, setInvalidFileFormat],
  );

  return (
    <>
      <Dialog
        closeButtonRef={closeButtonRef}
        closeIcon={true}
        closeOnDimmerClick={true}
        confirmButtonRef={saveButtonRef}
        headerIcons={resolveImageDialogIcons()}
        maxHeight={'expand'}
        width={700}
        onClose={() => props.onClose()}
      >
        {{
          header: resolvesTitle(),
          content: renderContent(),
          footer:
            poiHasThumbnail || isSharedView
              ? undefined
              : {
                  right: (
                    <DropdownButton
                      label={t('poiImageDialog.addImageButton', { ns: 'components' })}
                      options={[
                        {
                          label: t('poiImageDialog.sourceDropDown.computer', { ns: 'components' }),
                          value: 'Datorn',
                        },
                        {
                          label: t('poiImageDialog.sourceDropDown.fileManager', {
                            ns: 'components',
                          }),
                          value: 'Filhanteraren',
                        },
                      ]}
                      onSelect={handleAssociateImageSelection}
                    />
                  ),
                },
        }}
      </Dialog>

      {fileManagerDialog.render(
        <FileManagerSelectionDialog
          selectionMode="SingleFile"
          onCancel={() => {
            fileManagerDialog.hide();
          }}
          onSelect={async (folders, files) => {
            fileManagerDialog.hide();
            await onFileManagerImageSelected(files);
          }}
        />,
      )}

      {removeImageVerificationDialog.render((pos) => (
        <ConfirmationDialog
          maxWidth={500}
          negative={true}
          title={t('poiImageDialog.deleteImageVerification.title', { ns: 'components' })}
          onCancel={removeImageVerificationDialog.hide}
          onConfirm={async () => {
            removeImageVerificationDialog.hide();
            await removePoiImage();
          }}
        >
          {t('poiImageDialog.deleteImageVerification.message', { ns: 'components' })}
        </ConfirmationDialog>
      ))}

      <FileInput
        accept=".jpg,.jpeg,.png,.webp"
        data-testid="file-selection"
        multiple={false}
        ref={fileInputRef}
        onChange={onLocalImageSelected}
      />
    </>
  );
};

const ImageWrapper = styled(Stack)`
  position: relative;
`;

const SwitchOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  backdrop-filter: saturate(120%) blur(2px);
  user-select: none;
`;

const SwitchText = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const PreviewImageComponent = styled.img`
  cursor: pointer;
`;

const NoImageComponent = styled(Stack)`
  margin: 4em 0;
`;

const FileInput = styled.input.attrs(() => ({
  type: 'file',
}))`
  // Bump up specificity.
  // "display:none;" from global css used otherwise.
  && {
    ${reset}

    display: none;
  }
`;

const ErrorContainer = styled.div`
  padding: 0 6em;
`;

export { PoiImageDialog };
